// Search Container ///////////////////////////////////////////////////////////
//
// This file contains the logic for running searches.
//
// App -> Search Container
///////////////////////////////////////////////////////////////////////////////

// SETUP //////////////////////////////////////////////////////////////////////
// Third-party imports.
import { Col, } from 'reactstrap'
import { isCancel, } from 'axios'
import { useHistory, } from 'react-router-dom'
import PropTypes from 'prop-types'
import React, { Suspense, } from 'react'
import { useLocation, } from 'react-router-dom'

// Local imports.
import { directoryAdvancedSearch, } from '../api/DirectoryApi'
import { geocode, } from '../util/mapCalcs'
import { getCancelToken, } from '../api/ApiBase'
import { getErrorResponseString } from '../util/helpers'
import { initMapState, mapReducer, } from './SearchMapReducer'
import { mapOnlyProgramLocationData, mapProgramLocationData, } from '../util/programMappers'
import ErrorBoundary from '../components/ErrorBoundary'
import mapActions from './SearchMapActionTypes'
import SearchForm from '../components/Search/SearchForm'
import useLocationObj from '../hooks/useLocationObj'
import useMediaQuery from '../hooks/useMediaQuery'
import { generateMapStyle, generateFormStyle, generateAddress, generateMapResult, generateFilters, handleCancelToken, } from '../util/searchContainerHelpers'

// Local imports - lazy loading.
const ProgramsMap = React.lazy(() => import('../components/Maps/ProgramsMap'))

// Constants.
const mapContainerStyles = { height: '100%', width: '100%', }

// HELPER FUNCTIONS ///////////////////////////////////////////////////////////


// MAIN FUNCTION //////////////////////////////////////////////////////////////
const SearchContainer = ({
  activateProgram = null,
  activeProgramType = null,
  counties = [],
  eligibilityRequirements = [],
  emptyMessage = null,
  getUserLocation = null,
  industryTags = [],
  mapLibLoaded = false,
  mapLibLoadError = null,
  minimumEducationRequirements = [],
  programLevels = [],
  programTypes = [],
  resetSearchReturnData,
  returnLocationFilter = null,
  returnMapState = null,
  searched = false,
  searchFilters = null,
  searchResults = [],
  setActiveProgramType = null,
  setResults = null,
  setSearched =  null,
  setSearchFilters = null,
  setZipCode,
  userLocation = null,
}) => {
  const [ mapState, mapDispatch, ] = React.useReducer(mapReducer, {
    activeCategoryFilter : activeProgramType,
    bounds               : returnMapState ? returnMapState.viewBounds : null,
    center               : returnMapState ? returnMapState.viewCenter : null,
    locationFilter       : returnLocationFilter ? returnLocationFilter : null,
    zoom                 : returnMapState ? returnMapState.viewZoom : null,
  }, initMapState)

  const {
    activeProgramId : activeMapProgramId,
    activeProgramTypeName: activeMapProgramType,
    lastActiveProgramId: prevActiveId,
    locationFilter, 
    showMobileMap,
    viewBounds,
    viewCenter,
    viewZoom,
  } = mapState
  
  const [ searchError, setSearchError, ] = React.useState(null)
  const [ searching, setSearching, ] = React.useState(false)
  const { pathname, search, } = useLocationObj()
  const history = useHistory()

  // API request is in a callback, so keep a ref to the cancel token to keep it between renders.
  const cancelToken = React.useRef(null)
  const currentView = React.useRef(null)
  const currentLocationFilter = React.useRef(null)
  currentView.current = {
    viewBounds,
    viewZoom,
    viewCenter,
  }
  currentLocationFilter.current = locationFilter

  // Set layout based on media query.
  const isLargeDisplay = useMediaQuery('(min-width: 992px)')
  const isMobileDisplay = useMediaQuery('(max-device-width: 480px)')
  let mapStyle = generateMapStyle(isLargeDisplay, isMobileDisplay)
  let formStyle = generateFormStyle(isLargeDisplay, isMobileDisplay)

  // CALLBACK FUNCTIONS ///////////////////////////////////////////////////////
  // Location and map logic.
  const toggleMobileMap = React.useCallback(() => {
    mapDispatch(mapActions.toggleMobileMap())
  }, [ mapDispatch, ])

  const locationFilterReset = React.useCallback(() => {
    mapDispatch(mapActions.locationFilterReset())
  }, [ mapDispatch, ])

  const onGeocoded = React.useCallback((result) => {
    const mapResult = generateMapResult(result)
    mapDispatch(mapActions.locationFilterGeocoded(mapResult))
  }, [ mapDispatch, ])

  // Activate detail program.
  const activateDetailProgram = React.useCallback((id, type, program) => {
    activateProgram(id, type, program, currentView.current, currentLocationFilter.current)
  }, [ activateProgram, ])

  // Set results information in list.
  const searchResultsChanged = React.useCallback((searchResults) => {
    setResults(searchResults)
    if (mapLibLoaded) {
      const { locations, bounds, } = mapProgramLocationData(searchResults)
      mapDispatch(mapActions.locationsChanged(locations, null, bounds))
    }
  }, [ setResults, mapLibLoaded, mapDispatch, ])

  // Search logic.
  const doSearch = React.useCallback((filters) => {
    // Set the search filters.
    if (!filters) { filters = {} }
    setSearchFilters(filters)

    // Process the location.
    const address = generateAddress(filters.location)
    if (address && address.match(/^[0-9]+$/)) {
      setZipCode(address)
      geocode(address, onGeocoded)
    }
    else if (address) 
      geocode(address, onGeocoded)
    else 
      locationFilterReset()

    // Handle the cancel token.
    const token = cancelToken
    if (token && token.current) {
      token.current.cancel()
    }
    token.current = getCancelToken()

    // Internal function for success state?
    const searchSuccess = (response) => {
      const { data, } = response
      if (data) {
        if (data.errors) {
          setSearchError(data)
          console.error('Error requesting programs', data.errors)
        } else {
          searchResultsChanged(data)
          setSearchError('')
        }
      }
      setSearched(true)
      setSearching(false)
    }

    // Internal function for error state?
    const searchErrorResult = (response) => {
      if (!isCancel(response)){
        let errStr = getErrorResponseString(response)
        setSearching(false)
        setSearchError(errStr)
        setSearched(true)
        console.error('Error requesting programs', errStr)
      }
    }
    setSearching(true)

    // Scrub the location data for some reason.
    let { 
      location : _,
      ...remaining 
    } = filters

    // Scrub the type filter. We want to filter results on the JS side for now, not in the C# code.
    remaining['typeFilter'] = ''

    // Make an API call.
    directoryAdvancedSearch(remaining, searchSuccess, searchErrorResult, token.current)
  }, [ locationFilterReset, onGeocoded, searchResultsChanged, setSearchFilters, setSearched, setZipCode, ])


  // USE EFFECT ///////////////////////////////////////////////////////////////
  // Run an "onMount" function when the map library loads.
  React.useEffect(() => {
    // When the map library first loads, set the location data.
    if (mapLibLoaded) {
      // If we have boundaries, use them.
      const locations = mapOnlyProgramLocationData(searchResults)
      if (returnMapState && returnMapState.viewBounds) {
        mapDispatch(mapActions.locationsChanged(locations, activeProgramType, returnMapState, returnLocationFilter))
        resetSearchReturnData()
      }
      
      // Otherwise, set the default location.
      else {
        mapDispatch(mapActions.locationsChanged(locations, activeProgramType, null, returnLocationFilter))
      }

      // Run a "first time" search after the map finishes loading.
      doSearch()
    }
    else {
      // Don't do anything.
    }
  }, [ mapLibLoaded, ])

  // Update program type when program type changes.
  React.useEffect(() => {
    mapDispatch(mapActions.activeCategoryFilterChanged(activeProgramType))
  }, [ activeProgramType, mapDispatch, ])

  // Update the user location when the user location changes.
  React.useEffect(() => {
    if (getUserLocation && !userLocation) 
      getUserLocation()
  }, [ getUserLocation, userLocation , ])

  // Cancel API requests if ongoing.
  React.useEffect(() => {
    return handleCancelToken()
  }, [])

  // Run the search whenever the search inputs change.
  React.useEffect(() => {
    // If there's no search object, break out.
    if (!search) 
      return

    // Set the filters using the search object.
    const payload = generateFilters(search, userLocation)

    // Process the search.
    if (payload['category'])
      setActiveProgramType(payload['category'])
    if (payload['refreshSearch']) 
      doSearch(payload['filters'])
    if (payload['updatePath']) 
      history.push(pathname)
  }, [ pathname, search, history, doSearch, setActiveProgramType, userLocation, ])


  // RETURN HTML //////////////////////////////////////////////////////////////
  return <>
    <Col lg={6} xs={12} className={formStyle}>
      <h1 className={'hidden'}>CCW Directory</h1>
      <ErrorBoundary>
        <SearchForm 
          activeMapProgramId={activeMapProgramId}
          activeMapProgramType={activeMapProgramType}
          activeProgramType={activeProgramType}
          counties={counties}
          doSearch={doSearch}
          eligibilityRequirements={eligibilityRequirements}
          emptyMessage={emptyMessage}
          hideResults={!isLargeDisplay && showMobileMap}
          industryTags={industryTags}
          isLargeDisplay={isLargeDisplay}
          isMobileDisplay={isMobileDisplay}
          mapBounds={viewBounds}
          mapDispatch={mapDispatch}
          minimumEducationRequirements={minimumEducationRequirements}
          mobileMapShowing={showMobileMap}
          prevActiveId={prevActiveId}
          programLevels={programLevels}
          programTypes={programTypes}
          searched={searched}
          searchError={searchError}
          searchFilters={searchFilters}
          searching={searching}
          searchResults={searchResults}
          setActiveProgramId={activateDetailProgram}
          setActiveProgramType={setActiveProgramType}
          toggleMobileMap={toggleMobileMap}
          userLocation={userLocation}
        />
      </ErrorBoundary>
    </Col>
    <Col lg={6} xs={12} className={mapStyle}>
      {
        mapLibLoadError && <div>An error occurred loading the map</div>
      }
      {
        mapLibLoaded &&         
          <ErrorBoundary>
            <Suspense fallback={<div>Loading...</div>}>
              <ProgramsMap 
                activateProgram={activateDetailProgram}
                containerStyle={mapContainerStyles}
                dispatch={mapDispatch}
                isLargeDisplay={isLargeDisplay}
                isMobileDisplay={isMobileDisplay}
                mapLibLoaded={mapLibLoaded}
                pathname={pathname}
                state={mapState}
                userLocation={userLocation}
              />
            </Suspense>
          </ErrorBoundary>
      }
    </Col>
  </>
}

SearchContainer.propTypes = {
  activateProgram              : PropTypes.func,
  activeMapProgramId           : PropTypes.number,
  activeMapProgramType         : PropTypes.string,
  activeProgramType            : PropTypes.string,
  counties                     : PropTypes.array,
  eligibilityRequirements      : PropTypes.array,
  emptyMessage                 : PropTypes.string,
  getUserLocation              : PropTypes.func,
  industryTags                 : PropTypes.array,
  mapLibLoaded                 : PropTypes.bool,
  mapLibLoadError              : PropTypes.string,
  minimumEducationRequirements : PropTypes.array,
  programLevels                : PropTypes.array,
  programs                     : PropTypes.array,
  programTypes                 : PropTypes.array,
  resetSearchReturnData        : PropTypes.func,
  returnLocationFilter         : PropTypes.object,
  returnMapState               : PropTypes.object,
  searched                     : PropTypes.bool,
  searchFilters                : PropTypes.object,
  searchResults                : PropTypes.array,
  setActiveProgramType         : PropTypes.func,
  setResults                   : PropTypes.func,
  setSearched                  : PropTypes.func,
  setSearchFilters             : PropTypes.func,
  setZipCode                   : PropTypes.func,
  userLocation                 : PropTypes.object,
}

export default SearchContainer